import React, { useState } from 'react';
import { getUniversityMajorRecommendations, calculateSubjectScores } from '../utils/universityMajorRecommendation';
import { CLASSES } from '../data/classes';
import { Award, TrendingUp, BookOpen, Target, CheckCircle, AlertCircle, GraduationCap } from 'lucide-react';
import './StudentPortfolio.css';

const StudentPortfolio = ({ student, testResults, tests }) => {
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [groupBySubject, setGroupBySubject] = useState(false);

  // Validate inputs with comprehensive checks
  if (!student || !testResults || !tests) {
    return (
      <div className="student-portfolio">
        <div className="empty-state">
          <AlertCircle size={48} />
          <h3>No Data Available</h3>
          <p>Unable to load student portfolio data.</p>
        </div>
      </div>
    );
  }

  // Ensure testResults and tests are arrays
  const safeTestResults = Array.isArray(testResults) ? testResults : [];
  const safeTests = Array.isArray(tests) ? tests : [];
  const classSubjectById = new Map(CLASSES.map((cls) => [cls.id, cls.subject]));

  // Get student's test results
  const studentTestResults = safeTestResults.filter(r => r && r.studentId === student.id);

  if (studentTestResults.length === 0) {
    return (
      <div className="student-portfolio">
        <div className="empty-state">
          <AlertCircle size={48} />
          <h3>No Test Results</h3>
          <p>This student hasn't completed any tests yet.</p>
        </div>
      </div>
    );
  }

  // Calculate subject scores
  const subjectScores = calculateSubjectScores(studentTestResults, safeTests) || {};

  // Get university major recommendations
  const recommendations = getUniversityMajorRecommendations(student, safeTestResults, safeTests) || [];
  const topRecommendation = recommendations && recommendations.length > 0 ? recommendations[0] : null;
  const secondRecommendation = recommendations && recommendations.length > 1 ? recommendations[1] : null;

  // Get all unique subjects
  const subjects = Object.keys(subjectScores || {}).sort();

  // Group test results by subject
  const testsBySubject = {};
  studentTestResults.forEach(result => {
    const test = safeTests.find(t => t && t.id === result.testId);
    if (!test) return;

    const subject = test.subject || classSubjectById.get(test.classId);
    if (!subject) return;

    if (!testsBySubject[subject]) {
      testsBySubject[subject] = [];
    }

    testsBySubject[subject].push({
      ...result,
      testName: test.name,
      testDate: test.date,
      subject,
      questions: test.questions
    });
  });

  // Calculate overall statistics
  const totalTests = studentTestResults.length;
  const subjectValues = Object.values(subjectScores || {});
  const overallAverage = subjectValues.length > 0
    ? subjectValues.reduce((sum, score) => sum + score, 0) / subjectValues.length
    : 0;
  const testsAbove70 = studentTestResults.filter(r => r.percentage >= 70).length;
  const testsAbove80 = studentTestResults.filter(r => r.percentage >= 80).length;

  // Filter tests based on selected subject
  const filteredTests = selectedSubject === 'all'
    ? studentTestResults.map(result => {
        const test = safeTests.find(t => t && t.id === result.testId);
        const subject = test?.subject || classSubjectById.get(test?.classId) || 'Unknown';
        return {
          ...result,
          testName: test?.name || 'Unknown Test',
          testDate: test?.date,
          subject,
          questions: test?.questions || []
        };
      }).sort((a, b) => new Date(b.testDate) - new Date(a.testDate))
    : (testsBySubject[selectedSubject] || []).sort((a, b) => new Date(b.testDate) - new Date(a.testDate));

  const getScoreColor = (score) => {
    if (score >= 80) return '#10b981';
    if (score >= 70) return '#3b82f6';
    if (score >= 60) return '#f59e0b';
    return '#ef4444';
  };

  const getGradeLabel = (score) => {
    if (score >= 90) return 'A+';
    if (score >= 85) return 'A';
    if (score >= 80) return 'A-';
    if (score >= 75) return 'B+';
    if (score >= 70) return 'B';
    if (score >= 65) return 'B-';
    if (score >= 60) return 'C+';
    if (score >= 55) return 'C';
    if (score >= 50) return 'C-';
    return 'F';
  };

  return (
    <div className="student-portfolio">
      {/* Header Section */}
      <div className="portfolio-header">
        <div className="student-info">
          <div className="student-avatar">
            {(student.name || 'Student').split(' ').map(n => n[0]).join('')}
          </div>
          <div className="student-details">
            <h1>{student.name}</h1>
            <p className="student-meta">
              Grade {student.grade ?? 'N/A'}
              {student.section ? ` - Section ${student.section}` : ''}
              {student.email ? ` - ${student.email}` : ''}
            </p>
            <div className="student-stats-inline">
              <span className="stat-badge">
                <TrendingUp size={14} />
                {student.momentum}
              </span>
              <span className="stat-badge">
                <Award size={14} />
                Exam Readiness: {student.examReadiness}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <BookOpen size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Tests</p>
            <h3 className="stat-value">{totalTests}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Overall Average</p>
            <h3 className="stat-value">{Math.round(overallAverage)}%</h3>
            <p className="stat-sublabel">Grade {getGradeLabel(overallAverage)}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">High Performers</p>
            <h3 className="stat-value">{testsAbove80}</h3>
            <p className="stat-sublabel">{testsAbove70} tests above 70%</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)' }}>
            <Target size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Top Major Match</p>
            <h3 className="stat-value">{topRecommendation ? topRecommendation.matchScore : 0}%</h3>
            <p className="stat-sublabel">{topRecommendation ? topRecommendation.majorName : 'N/A'}</p>
          </div>
        </div>
      </div>

      {/* Subject Performance Overview */}
      <div className="section-card">
        <h2 className="section-title">
          <Award size={20} />
          Subject Performance
        </h2>
        <div className="subjects-grid">
          {subjects.map(subject => {
            const score = subjectScores[subject];
            const isKeySubject = topRecommendation?.requirements?.keySubjects?.includes(subject) || false;

            return (
              <div key={subject} className={`subject-card ${isKeySubject ? 'key-subject' : ''}`}>
                <div className="subject-header">
                  <h4>{subject}</h4>
                  {isKeySubject && topRecommendation && <span className="key-badge">Key for {topRecommendation.majorName}</span>}
                </div>
                <div className="subject-score-display">
                  <div className="score-circle" style={{
                    background: `conic-gradient(${getScoreColor(score)} ${score}%, #e2e8f0 ${score}%)`
                  }}>
                    <div className="score-circle-inner">
                      <span className="score-value">{score}%</span>
                      <span className="score-grade">{getGradeLabel(score)}</span>
                    </div>
                  </div>
                  <div className="subject-tests-count">
                    {testsBySubject[subject]?.length || 0} tests
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* University Major Recommendation */}
      {topRecommendation && (
        <div className="section-card recommendation-justification">
          <h2 className="section-title">
            <GraduationCap size={20} />
            Recommended University Major: {topRecommendation.majorName}
          </h2>

          {/* Why This Major Section */}
          <div className="why-section">
            <h3 className="why-title">Why This Major?</h3>
            <p className="why-text">{topRecommendation.why}</p>

            {/* Detailed Grade Analysis */}
            <div className="grade-analysis">
              <h4>Your Grades Support This Major:</h4>
              <div className="grade-highlights">
                {Object.entries(topRecommendation.requirements.subjects || {})
                  .sort((a, b) => b[1].weight - a[1].weight)
                  .slice(0, 3)
                  .map(([subject, req], idx) => {
                    const studentScore = subjectScores[subject] || 0;
                    const meetsRequirement = studentScore >= req.minScore;
                    return (
                      <div key={idx} className={`grade-highlight ${meetsRequirement ? 'meets' : 'below'}`}>
                        <div className="grade-highlight-header">
                          <span className="subject-name">{subject}</span>
                          <span className="grade-badge">{studentScore}%</span>
                        </div>
                        <div className="requirement-info">
                          <span>Required: {req.minScore}%</span>
                          <span className="weight-badge">{Math.round(req.weight * 100)}% importance</span>
                        </div>
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{
                              width: `${Math.min(studentScore, 100)}%`,
                              background: meetsRequirement ? '#10b981' : '#f59e0b'
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            <div className="major-details">
              <div className="detail-item">
                <strong>🎓 Top Universities:</strong>
                <p>{topRecommendation.universities?.slice(0, 4).join(', ')}</p>
              </div>
              <div className="detail-item">
                <strong>💼 Career Paths:</strong>
                <p>{topRecommendation.careerPaths?.join(', ')}</p>
              </div>
              <div className="detail-item">
                <strong>📊 Match Level:</strong>
                <p>{topRecommendation.level} ({topRecommendation.confidence} Confidence)</p>
              </div>
            </div>
          </div>

          {/* Second Major Recommendation */}
          {secondRecommendation && (
            <div className="second-major-section">
              <h3 className="second-major-title">Alternative Major Recommendation</h3>
              <div className="second-major-content">
                <div className="second-major-header">
                  <div>
                    <h4>{secondRecommendation.majorName}</h4>
                    <p className="second-major-why">{secondRecommendation.why}</p>
                  </div>
                  <div className="second-major-score">
                    <span className="score-value">{secondRecommendation.matchScore}%</span>
                    <span className="score-label">Match</span>
                  </div>
                </div>
                <div className="second-major-details">
                  <div className="detail-small">
                    <strong>Universities:</strong> {secondRecommendation.universities?.slice(0, 3).join(', ')}
                  </div>
                  <div className="detail-small">
                    <strong>Careers:</strong> {secondRecommendation.careerPaths?.slice(0, 2).join(', ')}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="justification-content">
            <div className="match-score-display">
              <div className="match-circle">
                <svg viewBox="0 0 100 100">
                  <circle cx="50" cy="50" r="45" fill="none" stroke="#e2e8f0" strokeWidth="10"/>
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    fill="none"
                    stroke="url(#gradient)"
                    strokeWidth="10"
                    strokeDasharray={`${2 * Math.PI * 45 * topRecommendation.matchScore / 100} ${2 * Math.PI * 45}`}
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#667eea"/>
                      <stop offset="100%" stopColor="#764ba2"/>
                    </linearGradient>
                  </defs>
                </svg>
                <div className="match-score-text">
                  <span className="match-score-value">{topRecommendation.matchScore}%</span>
                  <span className="match-score-label">Match</span>
                </div>
              </div>
              <div className="confidence-badge" style={{
                background: topRecommendation.confidence === 'High' ? '#10b981' :
                           topRecommendation.confidence === 'Medium' ? '#f59e0b' : '#6b7280'
              }}>
                {topRecommendation.confidence} Confidence
              </div>
            </div>

            <div className="justification-breakdown">
              <h3>Match Breakdown</h3>
              <div className="breakdown-items">
                <div className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-label">Subject Performance</span>
                    <span className="breakdown-score">{topRecommendation.breakdown.subjectMatch}%</span>
                  </div>
                  <div className="breakdown-bar">
                    <div className="breakdown-fill" style={{
                      width: `${topRecommendation.breakdown.subjectMatch}%`,
                      background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)'
                    }}></div>
                  </div>
                  <p className="breakdown-detail">
                    Based on {Object.keys(topRecommendation?.requirements?.subjects || {}).length} key subjects.
                    Top scores: {Object.entries(subjectScores || {})
                      .filter(([subject]) => Object.keys(topRecommendation.requirements.subjects || {}).includes(subject))
                      .sort((a, b) => b[1] - a[1])
                      .slice(0, 2)
                      .map(([subject, score]) => `${subject} (${score}%)`)
                      .join(', ')}
                  </p>
                  <div className="subject-scores-detail">
                    {Object.entries(topRecommendation.requirements.subjects || {})
                      .map(([subject, req]) => {
                        const studentScore = subjectScores[subject] || 0;
                        return (
                          <div key={subject} className="subject-score-mini">
                            <span className="mini-subject">{subject}</span>
                            <span className="mini-score" style={{
                              color: studentScore >= req.minScore ? '#10b981' : '#f59e0b'
                            }}>
                              {studentScore}% / {req.minScore}%
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-label">Strengths Alignment</span>
                    <span className="breakdown-score">{topRecommendation.breakdown.strengthsMatch}%</span>
                  </div>
                  <div className="breakdown-bar">
                    <div className="breakdown-fill" style={{
                      width: `${topRecommendation.breakdown.strengthsMatch}%`,
                      background: 'linear-gradient(90deg, #f093fb 0%, #f5576c 100%)'
                    }}></div>
                  </div>
                  <p className="breakdown-detail">
                    Matching strengths: {(student.strengths || []).filter(s =>
                      topRecommendation.requirements.strengths.some(rs =>
                        rs.toLowerCase().includes(s.toLowerCase()) || s.toLowerCase().includes(rs.toLowerCase())
                      )
                    ).join(', ') || 'None'}
                  </p>
                </div>

                <div className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-label">Interest Match</span>
                    <span className="breakdown-score">{topRecommendation.breakdown.interestsMatch}%</span>
                  </div>
                  <div className="breakdown-bar">
                    <div className="breakdown-fill" style={{
                      width: `${topRecommendation.breakdown.interestsMatch}%`,
                      background: 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
                    }}></div>
                  </div>
                  <p className="breakdown-detail">
                    Matching interests: {(student.interests || []).filter(i =>
                      topRecommendation.requirements.interests.some(ri =>
                        ri.toLowerCase().includes(i.toLowerCase()) || i.toLowerCase().includes(ri.toLowerCase())
                      )
                    ).join(', ') || 'None'}
                  </p>
                </div>

                <div className="breakdown-item">
                  <div className="breakdown-header">
                    <span className="breakdown-label">Academic Average</span>
                    <span className="breakdown-score">{topRecommendation.breakdown.averageMatch}%</span>
                  </div>
                  <div className="breakdown-bar">
                    <div className="breakdown-fill" style={{
                      width: `${topRecommendation.breakdown.averageMatch}%`,
                      background: 'linear-gradient(90deg, #fa709a 0%, #fee140 100%)'
                    }}></div>
                  </div>
                  <p className="breakdown-detail">
                    Overall: {Math.round(overallAverage)}% (Required: {topRecommendation.requirements.minOverallAverage}%)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detailed Test Results */}
      <div className="section-card">
        <div className="section-header">
          <div className="section-header-top">
            <h2 className="section-title">
              <BookOpen size={20} />
              All Test Results ({filteredTests.length})
            </h2>
            <button
              className={`group-toggle ${groupBySubject ? 'active' : ''}`}
              onClick={() => {
                setGroupBySubject(!groupBySubject);
                if (!groupBySubject) {
                  setSelectedSubject('all'); // Reset to all when grouping
                }
              }}
            >
              {groupBySubject ? '📋 List View' : '📁 Group by Subject'}
            </button>
          </div>
          {!groupBySubject && (
            <div className="subject-filter">
              <button
                className={selectedSubject === 'all' ? 'active' : ''}
                onClick={() => setSelectedSubject('all')}
              >
                All Subjects
              </button>
              {subjects.map(subject => (
                <button
                  key={subject}
                  className={selectedSubject === subject ? 'active' : ''}
                  onClick={() => setSelectedSubject(subject)}
                >
                  {subject}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Grouped by Subject View */}
        {groupBySubject ? (
          <div className="grouped-tests">
            {subjects.map(subject => {
              const subjectTests = testsBySubject[subject] || [];
              const subjectAvg = subjectScores[subject] || 0;
              const isKeySubject = topRecommendation?.requirements?.keySubjects?.includes(subject) || false;

              return (
                <div key={subject} className={`subject-group ${isKeySubject ? 'key-subject-group' : ''}`}>
                  <div className="subject-group-header">
                    <div className="subject-group-info">
                      <h3>{subject}</h3>
                      <span className="test-count">{subjectTests.length} test{subjectTests.length !== 1 ? 's' : ''}</span>
                      {isKeySubject && <span className="key-badge-small">⭐ Key Subject for {topRecommendation.majorName}</span>}
                    </div>
                    <div className="subject-avg-badge" style={{
                      background: getScoreColor(subjectAvg),
                      boxShadow: `0 4px 12px ${getScoreColor(subjectAvg)}30`
                    }}>
                      <span className="avg-label">Average</span>
                      <span className="avg-percentage">{subjectAvg}%</span>
                      <span className="avg-grade">{getGradeLabel(subjectAvg)}</span>
                    </div>
                  </div>

                  <div className="subject-tests-list">
                    {subjectTests.map((result, index) => (
                      <div key={index} className="test-result-card">
                        <div className="test-result-header">
                          <div className="test-info">
                            <h4>{result.testName}</h4>
                            <div className="test-meta">
                              <span className="test-date">
                                {new Date(result.testDate).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="test-score-badge" style={{
                            background: getScoreColor(result.percentage),
                            boxShadow: `0 4px 12px ${getScoreColor(result.percentage)}30`
                          }}>
                            <span className="score-percentage">{result.percentage}%</span>
                            <span className="score-grade">{getGradeLabel(result.percentage)}</span>
                          </div>
                        </div>

                        <div className="test-score-details">
                          <div className="score-bar">
                            <div className="score-bar-fill" style={{
                              width: `${result.percentage}%`,
                              background: getScoreColor(result.percentage)
                            }}></div>
                          </div>
                          <p className="score-text">
                            {result.score} / {result.maxScore} points
                          </p>
                        </div>

                        {/* Question Breakdown */}
                        {result.answers && result.answers.length > 0 && (
                          <div className="questions-breakdown">
                            <h5>Question Breakdown</h5>
                            <div className="questions-list">
                              {result.answers.map((answer, qIndex) => {
                                const question = (result.questions || [])[qIndex] || {};
                                const isCorrect = answer.earned === question.points;
                                const isPartial = answer.earned > 0 && answer.earned < question.points;

                                return (
                                  <div key={qIndex} className={`question-item ${isCorrect ? 'correct' : isPartial ? 'partial' : 'incorrect'}`}>
                                    <div className="question-header">
                                      <span className="question-number">Q{qIndex + 1}</span>
                                      <span className="question-topic">{question.topic || 'General'}</span>
                                      <span className="question-difficulty">{question.difficulty || 'Medium'}</span>
                                      <span className="question-score">
                                        {answer.earned || 0}/{question.points || 0} pts
                                      </span>
                                    </div>
                                    <p className="question-text">{question.text || 'Question text unavailable'}</p>
                                    {answer.struggle && answer.issue && (
                                      <div className="question-issue">
                                        <AlertCircle size={14} />
                                        <span>{answer.issue}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Regular List View */
          <div className="tests-list">
            {filteredTests.map((result, index) => {
              const isKeySubject = topRecommendation?.requirements?.keySubjects?.includes(result.subject) || false;

              return (
                <div key={index} className={`test-result-card ${isKeySubject ? 'key-test' : ''}`}>
                <div className="test-result-header">
                  <div className="test-info">
                    <h4>{result.testName}</h4>
                    <div className="test-meta">
                      <span className="test-subject">{result.subject}</span>
                      {isKeySubject && <span className="key-badge-small">Key Subject</span>}
                      <span className="test-date">
                        {new Date(result.testDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="test-score-badge" style={{
                    background: getScoreColor(result.percentage),
                    boxShadow: `0 4px 12px ${getScoreColor(result.percentage)}30`
                  }}>
                    <span className="score-percentage">{result.percentage}%</span>
                    <span className="score-grade">{getGradeLabel(result.percentage)}</span>
                  </div>
                </div>

                <div className="test-score-details">
                  <div className="score-bar">
                    <div className="score-bar-fill" style={{
                      width: `${result.percentage}%`,
                      background: getScoreColor(result.percentage)
                    }}></div>
                  </div>
                  <p className="score-text">
                    {result.score} / {result.maxScore} points
                  </p>
                </div>

                {/* Question Breakdown */}
                {result.answers && result.answers.length > 0 && (
                  <div className="questions-breakdown">
                    <h5>Question Breakdown</h5>
                    <div className="questions-list">
                      {result.answers.map((answer, qIndex) => {
                        const question = (result.questions || [])[qIndex] || {};
                        const isCorrect = answer.earned === question.points;
                        const isPartial = answer.earned > 0 && answer.earned < question.points;

                        return (
                          <div key={qIndex} className={`question-item ${isCorrect ? 'correct' : isPartial ? 'partial' : 'incorrect'}`}>
                            <div className="question-number">Q{qIndex + 1}</div>
                            <div className="question-details">
                              <p className="question-text">{question.text || 'Question'}</p>
                              <div className="question-meta">
                                <span className="question-topic">{question.topic || 'General'}</span>
                                <span className="question-difficulty">{question.difficulty || 'Medium'}</span>
                              </div>
                            </div>
                            <div className="question-score">
                              <span className={`score-earned ${isCorrect ? 'full' : isPartial ? 'partial' : 'none'}`}>
                                {answer.earned || 0}
                              </span>
                              <span className="score-total">/ {question.points || 0}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentPortfolio;



